import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import Modal from "./Modal";
import Button from "./Button";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

const ConfirmContext = createContext(null);

/**
 * แทนที่ window.confirm() — ใช้: const confirm = useConfirm(); if (await confirm("ข้อความ")) { ... }
 * options: { title, confirmText, cancelText, danger }
 */
export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const { t } = useTranslation();
  const resolveRef = useRef(null);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ message, options });
    });
  }, []);

  const handleClose = (result) => {
    setState(null);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        show={!!state}
        onHide={() => handleClose(false)}
        title={state?.options?.title || t("common.confirmTitle")}
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => handleClose(false)}>
              {state?.options?.cancelText || t("common.cancel")}
            </Button>
            <Button
              variant={state?.options?.danger ? "danger" : "primary"}
              onClick={() => handleClose(true)}
            >
              {state?.options?.confirmText || t("common.confirm")}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          {state?.options?.danger && (
            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-950/40 flex items-center justify-center shrink-0">
              <AlertTriangle size={20} className="text-red-500" />
            </div>
          )}
          <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">
            {state?.message}
          </p>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm ต้องถูกเรียกภายใน <ConfirmProvider>");
  return ctx;
};
